import { TourAnchor } from "training/anchors";
import {
  all,
  collectsPayment,
  continueBlockedByAbn,
  directPayEnabled,
  marksPaidViaCash,
  marksPaidViaCheckOnly,
  not,
  reachesCheckout,
  showsContinue,
  showsDiscount,
  showsDiscountExpiry,
  showsPlanSwitch,
  usesFinix,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

import { ESTIMATE_ACTIONS_MENU } from "./shared";

/** A reload on the patient page restarts at the Collect Payment click: that page cannot be opened by URL. */
const PATIENT_PAGE_RESUME = "m4.01-collect-payment";

/**
 * CLE-3015 Module 4: Collecting a payment at the desk. Text is the curriculum's (curriculum-v3,
 * 2026-10-21), verbatim, except where a comment says otherwise. Nothing here charges: Pay Now and the
 * Mark paid buttons are `look` steps, and the trainee leaves checkout with "Back to details".
 *
 * Reorder: 4.10 (Direct Pay, on the Estimates tab) is not here. It runs in Module 6 right before 6.4 (the
 * Reports tab), which is on the same hospital view, so the trainee goes from the patient page straight
 * back to the estimate for 4.11 instead of bouncing through the hospital view. Its id is unchanged; see
 * curriculum/module6.ts.
 *
 * Since the Estimate Details redesign (CLE-2961) "Accept payment" is the menu item "Collect Payment"; 4.1
 * opens the "..." menu first. Rows the curriculum gives one highlight and the page shows as several
 * elements are split at a sentence boundary (4.2, 4.6, 4.8).
 *
 * The $0 branch: a No Balance Due estimate has no Collect Payment, so 4.1–4.9 drop out (`collectsPayment`)
 * and the tour carries on at 4.11 / the cards.
 */
export const MODULE_4: ITourStep[] = [
  {
    id: "m4.01-collect-payment",
    module: 4,
    anchor: TourAnchor.CollectPayment,
    label: "Collect Payment",
    mode: "click",
    // The item logs in as the patient and opens /order/:externalId itself.
    advanceOn: "route",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    when: collectsPayment,
    text: "A patient is standing at your desk and wants to pay. This opens their own payment page for you, skipping the date-of-birth check they would get on their phone.",
  },
  {
    id: "m4.02-billing-statement",
    // After a page change (the patient page; checkout loads its card form).
    waitMs: 8000,
    module: 4,
    anchor: TourAnchor.BillingStatement,
    label: "ACCOUNT SUMMARY",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: collectsPayment,
    text: "This is the same page the patient saw in their text.",
  },
  {
    // Only on Patient Estimates and on Patient Balances with an insurance allowable (Home.tsx).
    id: "m4.02b-why-owe",
    module: 4,
    anchor: TourAnchor.WhyOwe,
    label: "Why do I owe this amount?",
    mode: "fill",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: collectsPayment,
    optional: true,
    waitMs: 1500,
    text: 'Open "Why do I owe this amount?" to show them the math: their deductible, coinsurance and copay against the contracted rate. It answers most questions before they ask.',
  },
  {
    id: "m4.03-pay-now-price",
    module: 4,
    anchor: TourAnchor.PayNowPrice,
    label: "Pay Now Price",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsDiscountExpiry),
    text: 'Paying before the visit earns the prompt-pay discount your hospital set. The crossed-out figure is the price without it, and "Discount expires on" says how long it lasts.',
  },
  {
    // The 4.3 branch "no expiry date": the curriculum's text without the expiry clause.
    id: "m4.03n-pay-now-price-no-expiry",
    module: 4,
    anchor: TourAnchor.PayNowPrice,
    label: "Pay Now Price",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsDiscount, not(showsDiscountExpiry)),
    text: "Paying before the visit earns the prompt-pay discount your hospital set. The crossed-out figure is the price without it.",
  },
  {
    // 4.4 is three clicks: "Payment Plan" shows the plan button, and "Pay in Full" puts the switch back —
    // left on "Payment Plan", Continue would go to the plan checkout, or stay greyed out with no plan.
    // The last sentence is not curriculum text.
    id: "m4.04-payment-plan-option",
    module: 4,
    anchor: TourAnchor.PaymentPlanOption,
    label: "Payment Plan",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsPlanSwitch),
    text: 'If the patient cannot pay it all, you can set up or change a plan right here at the desk. Click "Payment Plan".',
  },
  {
    // "Edit Payment Plan", or "Create Payment Plan" when the estimate has no plan.
    id: "m4.04b-payment-plan-button",
    module: 4,
    anchor: TourAnchor.PaymentPlanButton,
    label: "Payment Plan",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsPlanSwitch),
    // Absent when the account offers no plan option the editor can create (PaymentPlan.tsx editorOptions).
    optional: true,
    waitMs: 3000,
    text: "The patient only sees the plan option on their phone once a plan exists on the estimate.",
  },
  {
    // Not curriculum text.
    id: "m4.04c-pay-in-full-option",
    module: 4,
    anchor: TourAnchor.PayInFullOption,
    label: "Pay in Full",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsPlanSwitch),
    text: 'Click "Pay in Full" again to go on.',
  },
  {
    id: "m4.05-continue",
    module: 4,
    anchor: TourAnchor.Continue,
    label: "Continue",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "This goes to checkout. Click it, and stop on the next screen.",
  },
  {
    // Not curriculum text — the 4.5 branch "Continue hidden": explain why and skip checkout.
    id: "m4.05x-no-continue",
    module: 4,
    mode: "card",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, not(showsContinue)),
    text: 'Your hospital has turned off "Continue" on this page, or hides it until the visit has a date, so there is no checkout to show here. Patients still pay from the link in their text.',
  },
  {
    // Not curriculum text — the 4.5 branch "disabled while a required ABN is unsigned".
    id: "m4.05y-abn-unsigned",
    module: 4,
    mode: "card",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, showsContinue, continueBlockedByAbn),
    text: 'This estimate needs an ABN (Advance Beneficiary Notice) signed before payment, so "Continue" stays greyed out until the patient signs it on this page. The tour skips checkout.',
  },
  {
    // 4.6 is two highlights: the surcharge sentence moves onto the "Payment method fees" box it describes,
    // which only Finix hospitals with a credit-card fee show (the 4.6 branch).
    id: "m4.06-payment-method-fees",
    // After a page change (the patient page; checkout loads its card form).
    waitMs: 8000,
    module: 4,
    anchor: TourAnchor.PaymentMethodFees,
    label: "Payment method fees",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout, usesFinix),
    optional: true,
    text: "A credit card carries a surcharge where your hospital has one; debit and HSA or FSA cards do not, and the box says so.",
  },
  {
    id: "m4.06b-card-form",
    // After a page change (the patient page; checkout loads its card form).
    waitMs: 8000,
    module: 4,
    anchor: TourAnchor.CardForm,
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "Type the card here, or hand the keyboard to the patient. Do not enter a card now.",
  },
  {
    id: "m4.07-split-payment",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.SplitPayment,
    label: "Split payment between two cards",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "For a patient paying part on an HSA card and the rest on a credit card. Enter the first amount and the second fills itself.",
  },
  {
    id: "m4.08-terms",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.TermsCheckbox,
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "The patient must agree to the terms.",
  },
  {
    id: "m4.08b-pay-now",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.PayNow,
    label: "Pay Now",
    mode: "look",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "Pay Now charges the card and sends a receipt to their phone or email. Then the estimate shows Paid, and they arrive prepaid with nothing to discuss.",
  },
  {
    id: "m4.09-back-to-details",
    waitMs: 3000,
    module: 4,
    anchor: TourAnchor.BackToDetails,
    label: "Back to details",
    mode: "click",
    route: "patient",
    resumeAt: PATIENT_PAGE_RESUME,
    when: all(collectsPayment, reachesCheckout),
    text: "Leave without paying.",
  },
  {
    id: "m4.11-mark-paid-cash",
    module: 4,
    anchor: TourAnchor.MarkPaidCash,
    label: "Mark paid via cash",
    mode: "look",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    when: marksPaidViaCash,
    text: "A patient paid at the desk outside Clear Health. Mark it here so the estimate shows as paid and the texts stop. Do not click it now.",
  },
  {
    // Only when there is no cash item to point at: the check item sits right under it.
    id: "m4.11b-mark-paid-check",
    module: 4,
    anchor: TourAnchor.MarkPaidCheck,
    label: "Mark paid via check",
    mode: "look",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    when: marksPaidViaCheckOnly,
    text: "A patient paid at the desk outside Clear Health. Mark it here so the estimate shows as paid and the texts stop. Do not click it now.",
  },
  {
    // The curriculum's "Receipt" is the menu item "Download Receipt" since the redesign.
    id: "m4.12-receipt-card",
    module: 4,
    mode: "card",
    route: "estimate",
    text: 'After a payment, the "..." menu of the estimate gains "Download Receipt", and a partly paid estimate gains "Pay Additional Amount" and "Update Card".',
  },
  {
    id: "m4.13-tuesday-report-card",
    module: 4,
    mode: "card",
    route: "estimate",
    text: "Every payment that goes through Clear Health lands on your Tuesday report under the Account / Visit ID you typed. That is how your business office posts it.",
  },
  {
    id: "m4.14-evening-card",
    module: 4,
    mode: "card",
    route: "estimate",
    text: "Patients can pay from the text at any hour. Many pay in the evening after you have gone home.",
  },
];

/** 4.10, run from Module 6 (see the header). */
export const MODULE_4_DIRECT_PAY: ITourStep = {
  id: "m4.10-direct-pay",
  // First on the hospital view after a navigation.
  waitMs: 8000,
  module: 4,
  anchor: TourAnchor.DirectPay,
  label: "Direct Pay",
  mode: "look",
  route: "estimates",
  when: directPayEnabled,
  text: 'For money not tied to an estimate, such as an old balance or a walk-in. Name, date of birth, amount, and your account number, then "Continue to credit card". It makes its own estimate so it shows on your report. Look, do not fill it in.',
};
